/**
 * What a right-click on a folder in the rail offers.
 *
 * Everything the folder row's hover buttons do, plus the three things a mail
 * client's folder menu is actually used for: open it, mark all of it read, and
 * make a folder inside it. Rename and delete keep the same rule the buttons
 * follow - INBOX and the special-use folders carry neither, and a folder with
 * folders under it cannot be deleted - so the menu never offers what the
 * server would refuse.
 */
import { CheckCheck, FolderOpen, FolderPlus, PenLine, RefreshCw, Star, Trash2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { ImapFolder } from "../../../../lib/mail";
import { fmtNum } from "../../../ui";

export function ImapFolderItems({
  folder,
  close,
  openable,
  locked,
  hasChildren,
  onOpen,
  onMarkRead,
  onNewInside,
  onRename,
  onDelete,
  onRefresh,
  pin,
}: {
  folder: ImapFolder;
  close: () => void;
  /** Whether this operator may open it and it is a mailbox rather than a path segment. */
  openable: boolean;
  /** INBOX or a special-use folder: never renamed or deleted from here. */
  locked: boolean;
  hasChildren: boolean;
  onOpen: () => void;
  onMarkRead: () => void;
  onNewInside: () => void;
  onRename: () => void;
  onDelete: () => void;
  onRefresh: () => void;
  /** Pin to Favorites, or take the pin off. Absent: the rail keeps no Favorites. */
  pin?: { pinned: boolean; onToggle: () => void };
}) {
  const t = useT();

  const act = (fn: () => void) => {
    close();
    fn();
  };

  // A count the rail never read (a mailbox with many folders) is unknown, not
  // zero - "mark all read" stays on offer there, because there may be plenty.
  const nothingUnread = folder.unseen === 0;

  return (
    <>
      <div className="crm-menu-head">
        <div className="crm-menu-t">{folder.name}</div>
        <div className="crm-menu-s">
          {folder.messages === null
            ? folder.path
            : t("email.imap.menuCounts", { n: fmtNum(folder.messages), unseen: fmtNum(folder.unseen ?? 0) })}
        </div>
      </div>

      {openable ? (
        <button type="button" className="crm-mi" onClick={() => act(onOpen)}>
          <FolderOpen className="size-4" />
          {t("email.imap.open")}
        </button>
      ) : null}

      {pin && folder.selectable ? (
        <button type="button" className="crm-mi" onClick={() => act(pin.onToggle)}>
          <Star className="size-4" />
          {t(pin.pinned ? "mail.fav.unpin" : "mail.fav.pin")}
        </button>
      ) : null}

      {folder.selectable && !nothingUnread ? (
        <button type="button" className="crm-mi" onClick={() => act(onMarkRead)}>
          <CheckCheck className="size-4" />
          {t("email.imap.markAllRead")}
        </button>
      ) : null}

      {folder.selectable ? (
        <button type="button" className="crm-mi" onClick={() => act(onNewInside)}>
          <FolderPlus className="size-4" />
          {t("email.imap.newInside")}
        </button>
      ) : null}

      {locked ? null : (
        <button type="button" className="crm-mi" onClick={() => act(onRename)}>
          <PenLine className="size-4" />
          {t("email.imap.renameTitle")}
        </button>
      )}

      <button type="button" className="crm-mi" onClick={() => act(onRefresh)}>
        <RefreshCw className="size-4" />
        {t("email.imap.refresh")}
      </button>

      {locked || hasChildren ? null : (
        <>
          <div className="crm-menu-sep" />
          <button type="button" className="crm-mi crm-mi-danger" onClick={() => act(onDelete)}>
            <Trash2 className="size-4" />
            {t("email.imap.deleteTitle")}
          </button>
        </>
      )}
    </>
  );
}
